import "@/App.css";
import { useEffect } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Services from "@/pages/Services";
import ServiceDetail from "@/pages/ServiceDetail";
import Industries from "@/pages/Industries";
import Work from "@/pages/Work";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Contact from "@/pages/Contact";
import NotFound from "@/pages/NotFound";
import { useSEO } from "@/hooks/useSEO";

const ScrollToTop = () => {
  useSEO();
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname]);
  return null;
};

// One continuous backdrop shared by the page, with no section-level repeats.
function BusinessBackdrop() {
  const { scrollYProgress } = useScroll();
  const reduceMotion = useReducedMotion();
  const strategyOpacity = useTransform(scrollYProgress, [0, 0.25, 0.4], [1, 1, 0]);
  const teamOpacity = useTransform(scrollYProgress, [0.25, 0.4, 0.65, 0.8], [0, 1, 1, 0]);
  const growthOpacity = useTransform(scrollYProgress, [0.65, 0.8, 1], [0, 1, 1]);
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "-6%"]);
  return (
    <div className="business-backdrop" aria-hidden="true">
      <motion.div className="business-backdrop-images" style={{ y: reduceMotion ? 0 : y }}>
        <motion.img src="/photo-marketing.jpg" alt="" style={{ opacity: reduceMotion ? 0 : strategyOpacity }} />
        <motion.img src="/photo-social-team.jpg" alt="" style={{ opacity: reduceMotion ? 0 : teamOpacity }} />
        <motion.img src="/photo-analytics.jpg" alt="" style={{ opacity: reduceMotion ? 1 : growthOpacity }} />
      </motion.div>
      <div className="business-backdrop-wash" />
    </div>
  );
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true, autoRaf: true });
    window.__lenis = lenis;
    return () => {
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="App">
      <BusinessBackdrop />
      <BrowserRouter>
        <ScrollToTop />
        <Navbar />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<Services />} />
          <Route path="/services/:slug" element={<ServiceDetail />} />
          <Route path="/industries" element={<Industries />} />
          <Route path="/work" element={<Work />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        <Footer />
        <WhatsAppFloat />
        <Toaster position="bottom-center" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;
